import {
  Index,
  IndexBuildJobProgress,
  Job,
  ListPurgeJobProgress,
} from '../types';

export function formatNumber(value: number): string {
  return value.toLocaleString('en-GB');
}

/**
 * Describe how far through its build an index is
 */
export function describeIndexBuildProgress(job: Job | null): string {
  if (!job || job.status === 'queued') {
    return 'Waiting to start…';
  }

  const progress = job.progress as IndexBuildJobProgress | null;

  if (!progress || !progress.total) {
    return 'Starting…';
  }

  const processed = Math.min(progress.processed, progress.total);
  const percent = Math.floor((processed / progress.total) * 100);

  return `${percent}% (${formatNumber(processed)} of ${formatNumber(
    progress.total
  )} items)`;
}

/**
 * The name a deleted list had, if the job knows it
 */
export function listPurgeName(job: Job | null): string | null {
  return (job?.progress as ListPurgeJobProgress | null)?.name || null;
}

/**
 * Describe how far through a list's contents a purge is
 */
export function describeListPurgeProgress(job: Job | null): string {
  if (!job || job.status === 'queued') {
    return 'Waiting to start…';
  }

  const progress = job.progress as ListPurgeJobProgress | null;

  if (!progress || !progress.total) {
    return 'Starting…';
  }

  const processed = Math.min(progress.processed, progress.total);
  const percent = Math.floor((processed / progress.total) * 100);

  return `${percent}% (${formatNumber(processed)} of ${formatNumber(
    progress.total
  )} items)`;
}

/**
 * The resource a job works on, so that a later job for the same resource can
 * be recognised as superseding an earlier one
 */
export function jobSubject(job: Job): string {
  return `${job.type}:${job.indexId ?? job.listId ?? job.id}`;
}

/**
 * Whether a job is waiting or running
 */
export function isJobActive(job: Job): boolean {
  return job.status === 'queued' || job.status === 'running';
}

/**
 * Whether an unfinished job has already had an attempt fail, and is (waiting
 * to be) retried
 */
export function isJobRetrying(job: Job): boolean {
  return isJobActive(job) && !!job.error && job.attempts > 0;
}

/**
 * Dispatched on window when an index rebuild has been started from the
 * dashboard, with the rebuilt index as its detail, so that other components
 * showing the index or its jobs (the index page, the jobs menu) can catch up
 * without waiting for their next poll
 */
export const INDEX_REBUILD_STARTED_EVENT = 'jsonpad:index-rebuild-started';

export function announceIndexRebuild(index: Index): void {
  window.dispatchEvent(
    new CustomEvent<Index>(INDEX_REBUILD_STARTED_EVENT, { detail: index })
  );
}
